import React from 'react';

import { ChevronDownIcon } from '@adsk/alloy-react-icon';

import ErrorNode from './ErrorNode';
import LoaderNode from './LoaderNode';
import { ItemWrapper, ItemIconWrapper, ItemIcon, ItemLabel, ItemCheckbox, Separator } from './styles';
import * as TreeHelper from './treeHelper';
import { TreeNodeProps } from './types';

const TreeNode = ({
  node,
  disabled,
  expandable,
  expanded,
  selectable,
  selected,
  multiple,
  isActionNode,
  highlight,
  height,
  expandNodeFailureText,
  retryButtonText,
  treeListRef,
  viewItemRenderer,
  editItemRenderer,
  onReload,
  onToggle,
  onSelect,
  onContextMenu,
  matchAlloyStyle,
  showSkeletonLoading,
  allowReselectNodes,
  showDisabledSelection,
  ...rest
}: TreeNodeProps) => {
  const isLoadNode = node[TreeHelper.NODE_KEYS.NODE_ACTION_TYPE] === TreeHelper.NODE_KEYS.NODE_LOADER_KEY;
  const isErrorNode = node[TreeHelper.NODE_KEYS.NODE_ACTION_TYPE] === TreeHelper.NODE_KEYS.NODE_ERROR_KEY;
  const isSeparator = node[TreeHelper.NODE_KEYS.NODE_ACTION_TYPE] === TreeHelper.NODE_KEYS.NODE_SEPARATOR_KEY;

  const isSelectable =
    disabled || isActionNode || (!multiple && selected && !allowReselectNodes) || node.isEditing ? false : true;
  const showCheckbox = multiple && (selectable || (showDisabledSelection && !isActionNode));

  const handleToggleNode = (event: React.MouseEvent) => {
    if (event) event.stopPropagation();
    onToggle(node);
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    if (disabled || node.isEditing) return;

    event.stopPropagation();
    event.preventDefault();
    onContextMenu?.({ node, event });
  };

  const handleSelectNode = (event: React.MouseEvent) => {
    // https://reactjs.org/docs/legacy-event-pooling.html
    if (event && event.persist) event.persist();

    if (!selectable && expandable) {
      // Change to toggle node if node can expand but not selectable
      handleToggleNode(event);
      return;
    }

    if (event) event.stopPropagation();

    if (!isSelectable) return;

    onSelect(node, event);
  };

  if (isSeparator)
    return (
      <ItemWrapper noHover {...rest}>
        <Separator />
      </ItemWrapper>
    );

  if (isLoadNode) {
    return (
      <ItemWrapper noHover {...rest}>
        <LoaderNode showSkeletonLoading={showSkeletonLoading} height={height} />
      </ItemWrapper>
    );
  }

  if (isErrorNode)
    return (
      <ItemWrapper {...rest}>
        <ErrorNode
          expandNodeFailureText={expandNodeFailureText}
          retryButtonText={retryButtonText}
          onRetry={() => onReload(node.parentNode)}
        />
      </ItemWrapper>
    );

  return (
    <ItemWrapper
      highlight={highlight}
      selected={selected}
      selectable={isSelectable}
      disabled={disabled}
      onClick={handleSelectNode}
      onContextMenu={handleContextMenu}
      matchAlloyStyle={matchAlloyStyle}
      multiple={multiple}
      {...rest}
    >
      <ItemIconWrapper data-testid="node-icon" height={height} disabled={!expandable} onClick={handleToggleNode}>
        <ItemIcon
          data-testid={expanded ? 'node-icon-expand' : 'node-icon-collapse'}
          as={expandable ? ChevronDownIcon : 'div'}
          name="chevron-down"
          style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        />
      </ItemIconWrapper>
      {node.isEditing ? (
        editItemRenderer?.({ node, treeListRef, expanded, height })
      ) : (
        <ItemLabel height={height}>
          {showCheckbox && <ItemCheckbox data-testid="node-checkbox" checked={selected} disabled={disabled} />}
          {viewItemRenderer({ node, treeListRef, expanded, height })}
        </ItemLabel>
      )}
    </ItemWrapper>
  );
};

export default Object.assign(TreeNode, {
  ItemIconWrapper,
  ItemLabel
});
